import Accordion from '@src/components/reusable/Accordion';
import type { Order } from '@src/domains/orders';
import type { Sale } from '@src/domains/sales';
import type { DirectLink } from '@src/domains/direct-links';
import {
  PaymentMethodFormKind,
  needsPhoneVerification,
  resolvePaymentMethodForm,
  type PaymentCardParams,
  type PaymentOption,
} from '@src/domains/payments';
import type { PaymentMethodFormProps } from '@src/features/payments/options/types';
import React from 'react';
import { MdCreditCard } from 'react-icons/md';
import { Title, Header, Radio, Container } from './styles';
import PixIcon from '@src/icons/pix.svg';
import {
  getPaymentStatus,
  paymentErrorsSelector,
  paymentOngoingSelector,
  processPayment,
} from '@src/features/payments/store/payment';
import { useAppDispatch, useAppSelector, useTranslate, useWidgetParam } from '@src/core/hooks';
import { authSelector } from '@src/features/customers';
import PhoneVerificationSection from './PhoneVerificationSection';
import { DirectLinkCardForm } from '@src/features/payments/legacy';
import PixForm from './PixForm';
import BackdropLoading from '@src/components/ui2/BackdropLoading';
import { ExternalAuthenticationSetup } from '@src/features/payments/external-auth';

export type { PaymentMethodFormProps } from '@src/features/payments/options/types';

type PaymentMethodProps = {
  order?: Order;
  sale: Sale;
  directLink?: DirectLink;
  paymentOption: PaymentOption;
  onClick: (opt: PaymentOption) => void;
  selected?: boolean;
};

const PaymentMethod = (props: PaymentMethodProps) => {
  const { paymentOption, onClick = () => {}, selected = false, sale, order, directLink } = props;

  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();

  const { buyer } = useAppSelector(authSelector);
  const paymentOngoing = useAppSelector(paymentOngoingSelector);
  const errors = useAppSelector(paymentErrorsSelector);

  const inputClass = useWidgetParam('input-class') || 'input';

  const { code } = paymentOption.paymentMethod;
  const formKind = resolvePaymentMethodForm(code);
  const isCardPaymentMethod = formKind === PaymentMethodFormKind.Card;
  const isPixPaymentMethod = formKind === PaymentMethodFormKind.Pix;

  const CustomHeader = (_: boolean, title: string) => {
    return (
      <Header>
        <Radio type='radio' checked={selected} readOnly />

        {isCardPaymentMethod ? <MdCreditCard size={25} color='#4d4d4d' /> : null}

        {isPixPaymentMethod ? <PixIcon /> : null}

        <Title>
          {title}

          {paymentOption.paymentMethod.discountPercentage ? (
            <span>{` (${paymentOption.paymentMethod.discountPercentage.toString().replace('.', ',')}${i18n(
              'checkout_percentDiscount',
            )})`}</span>
          ) : null}
        </Title>
      </Header>
    );
  };

  const renderPaymentForm = (props: PaymentMethodFormProps) => {
    switch (formKind) {
      case PaymentMethodFormKind.Card:
        return <DirectLinkCardForm {...props} />;
      case PaymentMethodFormKind.Pix:
        return <PixForm {...props} />;
      default:
        return null;
    }
  };

  const handlePaymentSubmit = (paymentMethodId: string, cardValues?: PaymentCardParams) => {
    dispatch(
      processPayment({
        order,
        sale,
        directLink,
        paymentMethodId: paymentMethodId,
        paymentCard: cardValues,
      }),
    );
  };

  return (
    <Container $open={selected}>
      <Accordion
        open={selected}
        header={CustomHeader}
        title={paymentOption.paymentMethod.name}
        onClick={() => onClick(paymentOption)}
      >
        {paymentOngoing && <BackdropLoading />}

        {needsPhoneVerification({ paymentOption, phoneVerifiedAt: buyer?.phoneVerifiedAt }) ? (
          <PhoneVerificationSection />
        ) : (
          renderPaymentForm({
            cn: undefined, // cn
            order,
            sale,
            directLink,
            paymentOption,
            onSubmit: handlePaymentSubmit,
            saleStatusRequester: () => dispatch(getPaymentStatus({ directLink, sale, order })),
            contextErrors: errors,
            inputClass,
            unavailableMessage: i18n('checkout_unavailablePaymentMethod'),
          })
        )}
      </Accordion>

      <ExternalAuthenticationSetup paymentOption={paymentOption} />
    </Container>
  );
};

export default PaymentMethod;
